/* eslint-disable */
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Wallet,
  Download,
  CheckCircle2,
  AlertCircle,
  Plus,
  Trash2,
  X,
  Home,
  IndianRupee,
  QrCode,
  Copy,
  Clock,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { paymentsApi, startIciciPayment, downloadReceipt } from "./api";
import "./Payments.css";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN");

export default function PaymentsPage({ isAdmin = false }) {
  return (
    <div className="vpay-shell">
      <div className="vpay-head">
        <h1>
          <Wallet size={22} /> Payments
        </h1>
        <p className="vpay-sub">
          {isAdmin
            ? "Raise maintenance invoices and track what's paid and pending."
            : "Your maintenance invoices — pay online and download receipts."}
        </p>
      </div>
      {isAdmin ? <AdminPayments /> : <ResidentPayments />}
    </div>
  );
}

/* ============================ Resident ============================ */

function ResidentPayments() {
  const [tab, setTab] = useState("unpaid");
  const [invoices, setInvoices] = useState(null);
  const [error, setError] = useState("");
  const [payingId, setPayingId] = useState(null);
  const [params, setParams] = useSearchParams();
  // UPI modal state
  const [upiInvoice, setUpiInvoice] = useState(null); // invoice being paid via UPI
  const [methodPickerId, setMethodPickerId] = useState(null); // which invoice shows picker

  const banner = params.get("payment"); // success | failed

  const load = useCallback(() => {
    setInvoices(null);
    paymentsApi
      .listMyInvoices(tab)
      .then((d) => setInvoices(d.invoices || []))
      .catch((e) => {
        setInvoices([]);
        setError(e.message || "Could not load invoices");
      });
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const dismissBanner = () => {
    params.delete("payment");
    setParams(params, { replace: true });
  };

  const payIcici = async (inv) => {
    setMethodPickerId(null);
    setPayingId(inv.id);
    setError("");
    try {
      const res = await paymentsApi.pay(inv.id, "web");
      if (res.simulated) {
        params.set("payment", "success");
        setParams(params, { replace: true });
        setPayingId(null);
        setTab("paid");
        return;
      }
      startIciciPayment(res.payment);
    } catch (e) {
      setError(e.message || "Could not start the payment.");
      setPayingId(null);
    }
  };

  const openUpi = (inv) => {
    setMethodPickerId(null);
    setUpiInvoice(inv);
  };

  const receipt = async (inv) => {
    try {
      await downloadReceipt(inv.id, `receipt-${inv.villa_number}-${inv.period_label}.pdf`);
    } catch (e) {
      setError(e.message || "Receipt is not available yet.");
    }
  };

  return (
    <>
      {upiInvoice && (
        <UpiPaymentModal
          invoice={upiInvoice}
          onClose={() => { setUpiInvoice(null); load(); }}
          onPaid={() => { setUpiInvoice(null); setTab("paid"); }}
        />
      )}

      {banner === "success" && (
        <div className="vpay-banner ok">
          <CheckCircle2 size={18} /> Payment received — your invoice is now paid.
          <button onClick={dismissBanner} aria-label="Dismiss"><X size={15} /></button>
        </div>
      )}
      {banner === "failed" && (
        <div className="vpay-banner err">
          <AlertCircle size={18} /> Payment didn't go through. You can try again below.
          <button onClick={dismissBanner} aria-label="Dismiss"><X size={15} /></button>
        </div>
      )}

      <div className="vpay-tabs" role="tablist">
        <button className={tab === "unpaid" ? "active" : ""} onClick={() => setTab("unpaid")}>
          To pay
        </button>
        <button className={tab === "paid" ? "active" : ""} onClick={() => setTab("paid")}>
          Paid
        </button>
      </div>

      {error && <div className="vpay-error">{error}</div>}
      {invoices === null && <div className="vpay-empty">Loading…</div>}
      {invoices !== null && invoices.length === 0 && (
        <div className="vpay-empty">
          {tab === "unpaid" ? "You're all settled — no invoices to pay." : "No paid invoices yet."}
        </div>
      )}

      <div className="vpay-list">
        {(invoices || []).map((inv) => (
          <div key={inv.id} className="vpay-card">
            <div className="vpay-card-main">
              <div className="vpay-card-top">
                <span className="vpay-villa"><Home size={14} /> {inv.villa_number}</span>
                <span className="vpay-period">{inv.period_label}</span>
                <span className={`vpay-status ${inv.status.toLowerCase()}`}>{inv.status}</span>
              </div>
              <ul className="vpay-lines">
                {inv.lines.map((l) => (
                  <li key={l.id}>
                    <span>{l.description}</span>
                    <span>₹{fmt(l.amount_inr)}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="vpay-card-side">
              <div className="vpay-amount">₹{fmt(inv.amount_inr)}</div>
              {inv.status === "UNPAID" ? (
                methodPickerId === inv.id ? (
                  <div className="vpay-method-picker">
                    <button
                      className="vpay-method-btn"
                      disabled={payingId === inv.id}
                      onClick={() => payIcici(inv)}
                    >
                      🏦 Card / Net banking
                    </button>
                    <button
                      className="vpay-method-btn vpay-method-btn--upi"
                      onClick={() => openUpi(inv)}
                    >
                      <QrCode size={13} /> UPI — 0% fee
                    </button>
                    <button
                      className="vpay-method-cancel"
                      onClick={() => setMethodPickerId(null)}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    className="vpay-btn-primary"
                    disabled={payingId === inv.id}
                    onClick={() => setMethodPickerId(inv.id)}
                  >
                    {payingId === inv.id ? "Starting…" : "Pay now"}
                  </button>
                )
              ) : inv.receipt_available ? (
                <button className="vpay-btn-secondary" onClick={() => receipt(inv)}>
                  <Download size={14} /> Receipt
                </button>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// ── UPI payment modal ────────────────────────────────────────────────────────

function UpiPaymentModal({ invoice, onClose, onPaid }) {
  const [step, setStep] = useState("loading"); // loading | qr | submitted | paid
  const [upiData, setUpiData] = useState(null); // {payment_id, upi_url, amount_inr, merchant_name}
  const [utr, setUtr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    paymentsApi.payUpi(invoice.id)
      .then((data) => {
        setUpiData(data);
        setStep("qr");
      })
      .catch((e) => {
        if (e?.status === 503) {
          setError("UPI payments are not configured yet. Please use the gateway option.");
        } else {
          setError(e?.detail || e?.message || "Could not start UPI payment.");
        }
        setStep("error");
      });
  }, [invoice.id]);

  // Poll invoice status while modal is open (after UTR submitted)
  useEffect(() => {
    if (step !== "submitted") return;
    pollRef.current = setInterval(async () => {
      try {
        const invs = await paymentsApi.listMyInvoices("unpaid");
        const still = (invs.invoices || []).find((i) => i.id === invoice.id);
        if (!still) {
          clearInterval(pollRef.current);
          setStep("paid");
          setTimeout(onPaid, 1500);
        }
      } catch (_) {}
    }, 10_000);
    return () => clearInterval(pollRef.current);
  }, [step, invoice.id, onPaid]);

  const submitUtr = async () => {
    if (!/^\d{12}$/.test(utr.trim())) {
      setError("Enter the 12-digit UTR shown in your payment app.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await paymentsApi.submitUtr(upiData.payment_id, utr.trim());
      setStep("submitted");
    } catch (e) {
      setError(e?.detail || e?.message || "Could not submit UTR.");
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = () => {
    if (!upiData?.upi_url) return;
    navigator.clipboard.writeText(upiData.upi_url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="vpay-modal-backdrop" onClick={onClose}>
      <div className="vpay-modal vpay-upi-modal" onClick={(e) => e.stopPropagation()}>
        <div className="vpay-modal-head">
          <h2><QrCode size={18} /> Pay via UPI</h2>
          <button onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>

        {step === "loading" && <div className="vpay-empty">Generating payment…</div>}

        {step === "error" && (
          <div className="vpay-upi-body">
            <div className="vpay-banner err"><AlertCircle size={16} /> {error}</div>
          </div>
        )}

        {step === "qr" && upiData && (
          <div className="vpay-upi-body">
            <p className="vpay-upi-amount">₹{fmt(upiData.amount_inr)}</p>
            <p className="vpay-upi-hint">Scan with Google Pay, PhonePe, or any UPI app</p>
            <div className="vpay-qr-wrap">
              <QRCodeSVG value={upiData.upi_url} size={200} level="M" />
            </div>
            <p className="vpay-upi-hint vpay-upi-hint--small">
              Or{" "}
              <a href={upiData.upi_url} className="vpay-upi-link">open in UPI app</a>
              {" "}·{" "}
              <button className="vpay-upi-copy" onClick={copyLink}>
                <Copy size={12} /> {copied ? "Copied!" : "Copy link"}
              </button>
            </p>

            <div className="vpay-utr-section">
              <label className="vpay-utr-label">
                After paying, enter the 12-digit UTR from your payment app
              </label>
              <div className="vpay-utr-row">
                <input
                  className="vpay-utr-input"
                  placeholder="e.g. 123456789012"
                  maxLength={12}
                  value={utr}
                  onChange={(e) => setUtr(e.target.value.replace(/\D/g, ""))}
                />
                <button
                  className="vpay-btn-primary"
                  disabled={submitting || utr.length !== 12}
                  onClick={submitUtr}
                >
                  {submitting ? "Submitting…" : "I've paid"}
                </button>
              </div>
              {error && <div className="vpay-error">{error}</div>}
            </div>
          </div>
        )}

        {step === "submitted" && (
          <div className="vpay-upi-body vpay-upi-pending-body">
            <Clock size={36} className="vpay-upi-clock" />
            <p className="vpay-upi-pending-title">UTR submitted — under review</p>
            <p className="vpay-upi-pending-sub">
              An admin will verify your payment and mark the invoice paid. You'll see it
              in your "Paid" tab once confirmed.
            </p>
            <button className="vpay-btn-secondary" onClick={onClose}>Close</button>
          </div>
        )}

        {step === "paid" && (
          <div className="vpay-upi-body vpay-upi-pending-body">
            <CheckCircle2 size={36} className="vpay-upi-paid-icon" />
            <p className="vpay-upi-pending-title">Payment verified!</p>
            <p className="vpay-upi-pending-sub">Your invoice has been marked as paid.</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================ Admin ============================ */

function AdminPayments() {
  const [adminTab, setAdminTab] = useState("invoices"); // invoices | upi
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [upiPending, setUpiPending] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [verifyingId, setVerifyingId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    paymentsApi.adminSummary().then(setSummary).catch(() => {});
    paymentsApi.listInvoices({ limit: 50 }).then((d) => setInvoices(d.invoices || [])).catch(() => {});
    paymentsApi.upiPending().then(setUpiPending).catch(() => {});
  }, [reloadKey]);

  const reload = () => setReloadKey((k) => k + 1);

  const verifyUtr = async (paymentId) => {
    setVerifyingId(paymentId);
    try {
      await paymentsApi.verifyUtr(paymentId);
      reload();
    } catch (e) {
      alert(e?.detail || e?.message || "Could not verify UTR");
    } finally {
      setVerifyingId(null);
    }
  };

  return (
    <>
      <div className="vpay-summary">
        <div className="vpay-stat">
          <span className="vpay-stat-num">₹{fmt(summary?.pending_total_inr)}</span>
          <span className="vpay-stat-lbl">Pending ({fmt(summary?.unpaid_count)} invoices)</span>
        </div>
        <div className="vpay-stat">
          <span className="vpay-stat-num">₹{fmt(summary?.paid_total_inr)}</span>
          <span className="vpay-stat-lbl">Collected ({fmt(summary?.paid_count)} invoices)</span>
        </div>
        <button className="vpay-btn-primary vpay-raise" onClick={() => setShowForm(true)}>
          <Plus size={15} /> Raise invoices
        </button>
      </div>

      <div className="vpay-tabs">
        <button className={adminTab === "invoices" ? "active" : ""} onClick={() => setAdminTab("invoices")}>
          Invoices
        </button>
        <button className={adminTab === "upi" ? "active" : ""} onClick={() => setAdminTab("upi")}>
          UPI pending {upiPending.length > 0 && <span className="vpay-upi-badge">{upiPending.length}</span>}
        </button>
      </div>

      {adminTab === "invoices" && (
        <div className="vpay-admin-list">
          {invoices.length === 0 && <div className="vpay-empty">No invoices raised yet.</div>}
          {invoices.map((inv) => (
            <div key={inv.id} className="vpay-admin-row">
              <span className="vpay-villa"><Home size={13} /> {inv.villa_number}</span>
              <span className="vpay-period">{inv.period_label}</span>
              <span className={`vpay-status ${inv.status.toLowerCase()}`}>{inv.status}</span>
              <span className="vpay-amount-sm">₹{fmt(inv.amount_inr)}</span>
            </div>
          ))}
        </div>
      )}

      {adminTab === "upi" && (
        <div className="vpay-admin-list">
          {upiPending.length === 0 && (
            <div className="vpay-empty">No UPI payments awaiting verification.</div>
          )}
          {upiPending.map((p) => (
            <div key={p.payment_id} className="vpay-admin-row vpay-upi-pending-row">
              <span className="vpay-villa"><Home size={13} /> {p.invoice?.villa_number}</span>
              <span className="vpay-period">{p.invoice?.period_label}</span>
              <span className="vpay-amount-sm">₹{fmt(p.amount_inr)}</span>
              <span className="vpay-utr-chip">UTR: {p.utr}</span>
              <button
                className="vpay-btn-primary vpay-verify-btn"
                disabled={verifyingId === p.payment_id}
                onClick={() => verifyUtr(p.payment_id)}
              >
                {verifyingId === p.payment_id ? "Verifying…" : "Verify & mark paid"}
              </button>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <RaiseInvoicesModal
          onClose={() => setShowForm(false)}
          onDone={() => {
            setShowForm(false);
            reload();
          }}
        />
      )}
    </>
  );
}

function RaiseInvoicesModal({ onClose, onDone }) {
  const [periodLabel, setPeriodLabel] = useState("");
  const [frequency, setFrequency] = useState("ONE_TIME");
  const [rate, setRate] = useState(3);
  const [scope, setScope] = useState("ALL");
  const [villas, setVillas] = useState([]);
  const [selVillas, setSelVillas] = useState([]);
  const [adhoc, setAdhoc] = useState([]);
  const [recurring, setRecurring] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    paymentsApi.listVillas().then(setVillas).catch(() => {});
  }, []);

  const toggleVilla = (id) =>
    setSelVillas((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const addAdhoc = () => setAdhoc((a) => [...a, { description: "", amount_rupees: "" }]);
  const setAdhocField = (i, k, v) =>
    setAdhoc((a) => a.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const removeAdhoc = (i) => setAdhoc((a) => a.filter((_, idx) => idx !== i));

  const submit = async () => {
    setError("");
    if (!recurring && periodLabel.trim().length < 2) {
      setError("Enter a period label (e.g. Q2 2026).");
      return;
    }
    if (scope === "SELECTED" && selVillas.length === 0) {
      setError("Select at least one villa.");
      return;
    }
    setBusy(true);
    try {
      const body = {
        period_label: recurring ? frequency : periodLabel.trim(),
        frequency,
        rate_rupees_per_sqft: Number(rate) || 0,
        scope,
        is_recurring: recurring,
      };
      if (scope === "SELECTED") body.villa_ids = selVillas;
      const ah = adhoc
        .filter((r) => r.description.trim() && Number(r.amount_rupees) > 0)
        .map((r) => ({ description: r.description.trim(), amount_rupees: Number(r.amount_rupees) }));
      if (ah.length) body.adhoc_lines = ah;
      const res = await paymentsApi.createBatch(body);
      setResult(res);
    } catch (e) {
      setError(e.message || "Could not raise invoices.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="vpay-modal-backdrop" onClick={onClose}>
      <div className="vpay-modal" onClick={(e) => e.stopPropagation()}>
        <div className="vpay-modal-head">
          <h2>Raise maintenance invoices</h2>
          <button onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>

        {result ? (
          <div className="vpay-result">
            <CheckCircle2 size={36} className="vpay-result-icon" />
            <p><strong>{result.created}</strong> invoice(s) raised.</p>
            {result.skipped > 0 && <p className="muted">{result.skipped} already existed for this period.</p>}
            {result.skipped_no_area?.length > 0 && (
              <p className="muted">{result.skipped_no_area.length} villa(s) skipped — no area on file.</p>
            )}
            {result.skipped_no_residents?.length > 0 && (
              <p className="muted">{result.skipped_no_residents.length} villa(s) have no residents to notify.</p>
            )}
            <button className="vpay-btn-primary" onClick={onDone}>Done</button>
          </div>
        ) : (
          <div className="vpay-form">
            <label className="vpay-check">
              <input type="checkbox" checked={recurring} onChange={(e) => setRecurring(e.target.checked)} />
              <span>Recurring — auto-raise this charge every period</span>
            </label>

            <div className="vpay-row">
              <div className="vpay-field">
                <label>Frequency</label>
                <select value={frequency} onChange={(e) => setFrequency(e.target.value)}>
                  <option value="ONE_TIME">One-time</option>
                  <option value="MONTHLY">Monthly</option>
                  <option value="QUARTERLY">Quarterly</option>
                </select>
              </div>
              {!recurring && (
                <div className="vpay-field">
                  <label>Period label</label>
                  <input value={periodLabel} onChange={(e) => setPeriodLabel(e.target.value)} placeholder="Q2 2026" />
                </div>
              )}
              <div className="vpay-field">
                <label>Rate (₹/sqft)</label>
                <input type="number" min="0" step="0.5" value={rate} onChange={(e) => setRate(e.target.value)} />
              </div>
            </div>

            <div className="vpay-field">
              <label>Who to bill</label>
              <div className="vpay-toggle">
                <button className={scope === "ALL" ? "active" : ""} onClick={() => setScope("ALL")}>All villas</button>
                <button className={scope === "SELECTED" ? "active" : ""} onClick={() => setScope("SELECTED")}>Selected villas</button>
              </div>
            </div>

            {scope === "SELECTED" && (
              <div className="vpay-villa-picker">
                {villas.map((v) => (
                  <label key={v.id} className={selVillas.includes(v.id) ? "sel" : ""}>
                    <input type="checkbox" checked={selVillas.includes(v.id)} onChange={() => toggleVilla(v.id)} />
                    {v.villa_number}
                  </label>
                ))}
              </div>
            )}

            <div className="vpay-field">
              <label>Ad-hoc charges (optional)</label>
              {adhoc.map((row, i) => (
                <div key={i} className="vpay-adhoc-row">
                  <input
                    placeholder="Description"
                    value={row.description}
                    onChange={(e) => setAdhocField(i, "description", e.target.value)}
                  />
                  <input
                    type="number" min="0" placeholder="₹"
                    value={row.amount_rupees}
                    onChange={(e) => setAdhocField(i, "amount_rupees", e.target.value)}
                  />
                  <button onClick={() => removeAdhoc(i)} aria-label="Remove"><Trash2 size={14} /></button>
                </div>
              ))}
              <button className="vpay-add-line" onClick={addAdhoc}><Plus size={13} /> Add charge</button>
            </div>

            {error && <div className="vpay-error">{error}</div>}
            <div className="vpay-modal-actions">
              <button className="vpay-btn-secondary" onClick={onClose}>Cancel</button>
              <button className="vpay-btn-primary" disabled={busy} onClick={submit}>
                {busy ? "Raising…" : "Raise invoices"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
